import { createGlobalState } from '@vueuse/core';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { scheduledMessagesClient } from '~/services';
import { ref } from 'vue';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { catchRequestError } from '~/utils/request';
import { getClientTz } from '~/utils/date';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { useRoute } from 'vue-router';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

export const useScheduledMessages = createGlobalState(() => {
    const { currentOrgUnit } = useCurrentGroup();

    const listCancelKey = Symbol('fetch-scheduled-messages');

    const { loading, list: scheduledMessages, fetchParams, fetchRecords, error } = useCursorPagination();

    const sending = ref(false);
    const sendingError = ref('');
    const activeMessage = ref();

    const route = useRoute();

    const defaultFetchParams = () => {
        return {
            sort_field: 'send_at',
            sort_dir: 'asc',
            per_page: 10,
            cursor: null,
        };
    };

    const fetchFirstPage = (context) => {
        fetchParams.value.cursor = null;
        scheduledMessages.value = [];

        return fetchData(context);
    };

    const fetchData = (context) => {
        return fetchRecords(
            scheduledMessagesClient.getScheduledMessages(context, fetchParams.value, { cancelKey: listCancelKey })
        );
    };

    const resetFilters = () => {
        fetchParams.value = defaultFetchParams();
    };

    const deleteScheduledMessage = async (context, messageId, contactId) => {
        try {
            await scheduledMessagesClient.deleteScheduledMessage(context, contactId, messageId);
        } catch (e) {
            if (e?.response?.status !== HTTP_NOT_FOUND) {
                throw e;
            }
        }
    };

    const createScheduledMessage = (context, messageData) => {
        return scheduledMessagesClient.createScheduledMessage(context, messageData.contact_id, {
            body: messageData.body,
            send_at: toUTCDateTimeFormat(dayjs(messageData.send_at).tz(getClientTz())),
            media_url: messageData.media_url,
            media_content_type: messageData.media_content_type,
        });
    };

    const editScheduledMessage = async (context, messageData) => {
        sending.value = true;
        sendingError.value = '';

        try {
            await deleteScheduledMessage(context, messageData.id, messageData.contact_id);
            const response = await createScheduledMessage(context, messageData);

            // Replace the old message with the updated one in the list
            if (response && response.data) {
                const index = scheduledMessages.value.findIndex((message) => message.id === messageData.id);
                if (index !== -1) {
                    scheduledMessages.value.splice(index, 1, response.data);
                } else {
                    scheduledMessages.value = [...scheduledMessages.value, response.data];
                }
            }

            return response.data;
        } catch (e) {
            if (e?.response?.status !== HTTP_NOT_FOUND) {
                sendingError.value = catchRequestError(e);
            }
        } finally {
            sending.value = false;
        }
    };

    const setActiveMessage = () => {
        const message = scheduledMessages.value.find(
            (message) => message.id === Number(route.params.scheduledMessageId)
        );

        activeMessage.value = message
            ? {
                  ...message,
                  send_at: dayjs.utc(message.send_at).tz(getClientTz()).toDate(),
                  contact_id: message.contact.id,
              }
            : null;
    };

    const scheduledMessagesCount = ref(0);
    const countCancelKey = Symbol('count-scheduled-messages');

    const fetchScheduledMessagesCount = async (context) => {
        if (currentOrgUnit.value) {
            return;
        }

        try {
            const response = await scheduledMessagesClient.getScheduledMessagesCount(context, {
                cancelKey: countCancelKey,
            });
            scheduledMessagesCount.value = response.data.count || 0;
        } catch (error) {
            catchRequestError(error);
        }
    };

    return {
        fetchParams,
        loading,
        scheduledMessages,
        activeMessage,
        error,
        sending,
        sendingError,

        fetchFirstPage,
        fetchData,
        resetFilters,
        editScheduledMessage,
        deleteScheduledMessage,
        createScheduledMessage,

        setActiveMessage,

        fetchScheduledMessagesCount,
        scheduledMessagesCount,
    };
});
